import React, { useRef, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  downloadRelatorioCsvPlacasResumo,
  downloadRelatorioXlsxPlacasResumo,
} from '../../../../api/contac/contacReportsPlacasApi';
import FullScreenLoader from '../../../loading/FullScreenLoader';
import './ModalReports.css';
import '../filtro/ModalFiltro.css';
import '../../../../shared/ui/modal-close-button.css'; // X padrão do sistema

interface ModalReportsPlacaProps {
  isOpen: boolean;
  onClose: () => void;

  initialPlacas?: string[];

  dataInicio: string;
  dataFim: string;
  baseTag?: string;
}

const EMPTY: string[] = [];

const ModalReportsPlaca: React.FC<ModalReportsPlacaProps> = ({
  isOpen,
  onClose,
  initialPlacas,
  dataInicio,
  dataFim,
  baseTag,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const safeInitial = initialPlacas ?? EMPTY;
  const initialKey = safeInitial.join('|');

  const [placas, setPlacas] = useState<string[]>(safeInitial);
  const [placaInput, setPlacaInput] = useState<string>('');

  const [codigoVoluntario, setCodigoVoluntario] = useState<string>('');
  const [categoria, setCategoria] = useState<string>('');
  const [regional, setRegional] = useState<string>('');
  const [cpfAssociado, setCpfAssociado] = useState<string>('');
  const [associadoNome, setAssociadoNome] = useState<string>('');

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    setPlacas(safeInitial);
    setPlacaInput('');

    setCodigoVoluntario('');
    setCategoria('');
    setRegional('');
    setCpfAssociado('');
    setAssociadoNome('');
    // A dependência proposital é o initialKey (conteúdo da lista de placas), não o array
    // safeInitial: o pai pode entregar um array novo a cada render e depender da referência
    // faria o efeito re-executar sem parar (setPlacas -> render -> novo array -> efeito).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialKey]);

  if (!isOpen) return null;

  const formatarPeriodoCurto = (data: string) => {
    const meses = ['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez'];
    const [ano, mes] = data.split('-');
    return `${meses[Number(mes) - 1]}/${ano}`;
  };

  const normalizePlaca = (v: string) =>
    String(v ?? '')
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');

  const addPlaca = (raw: string) => {
    const v = normalizePlaca(raw);
    if (!v) return;
    setPlacas((prev) => (prev.includes(v) ? prev : [...prev, v]));
  };

  const removePlaca = (v: string) => setPlacas((prev) => prev.filter((x) => x !== v));

  const handleDownload = async () => {
    try {
      setLoading(true);

      await downloadRelatorioCsvPlacasResumo({
        dataInicio,
        dataFim,
        base_tag: baseTag,
        placas: placas.length > 0 ? placas : undefined,

        codigoVoluntario: codigoVoluntario.trim() || undefined,
        categoria: categoria.trim() || undefined,
        regional: regional.trim() || undefined,
        cpfAssociado: cpfAssociado.trim() || undefined,
        associadoNome: associadoNome.trim() || undefined,
      });

      toast.success('Relatório CSV gerado com sucesso!');
      onClose();
    } catch {
      toast.error('Erro ao gerar CSV');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadXlsx = async () => {
    try {
      setLoading(true);

      await downloadRelatorioXlsxPlacasResumo({
        dataInicio,
        dataFim,
        base_tag: baseTag,
        placas: placas.length > 0 ? placas : undefined,

        codigoVoluntario: codigoVoluntario.trim() || undefined,
        categoria: categoria.trim() || undefined,
        regional: regional.trim() || undefined,
        cpfAssociado: cpfAssociado.trim() || undefined,
        associadoNome: associadoNome.trim() || undefined,
      });

      toast.success('Relatório XLSX gerado com sucesso!');
      onClose();
    } catch {
      toast.error('Erro ao gerar XLSX');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="modal-filtro-overlay">
        <div className="modal-filtro-content" ref={modalRef}>
          <div className="filtro-modal-card">
            
            {/* Header */}
            <div className="filtro-modal-header">
              <div>
                <h2 className="filtro-modal-title">Gerar Relatório de Placas (Resumo)</h2>
                <p className="filtro-modal-subtitle">
                  {formatarPeriodoCurto(dataInicio)} – {formatarPeriodoCurto(dataFim)}
                </p>
              </div>
              <button className="close-button" onClick={onClose} disabled={loading}>&times;</button>
            </div>

            {/* Body */}
            <div className="filtro-modal-body" style={{ maxHeight: 'calc(80vh - 120px)', overflowY: 'auto' }}>
              <div className="filtro-section">
                <h3 className="filtro-section-title">Filtros (Opcional)</h3>
                
                <div className="filtro-grid-2">
                  
                  {/* Placas input (Full width in the 2-column grid) */}
                  <div className="filtro-field" style={{ gridColumn: 'span 2' }}>
                    <label>Placas</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        type="text"
                        className="input-filtro"
                        placeholder="Ex: ABC1D23"
                        value={placaInput}
                        onChange={(e) => setPlacaInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter') return;
                          addPlaca(placaInput);
                          setPlacaInput('');
                        }}
                        autoFocus
                        disabled={loading}
                      />
                      <button
                        type="button"
                        className="filtro-btn-secondary"
                        onClick={() => {
                          addPlaca(placaInput);
                          setPlacaInput('');
                        }}
                        disabled={loading}
                        style={{ height: '42px', padding: '0 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap' }}
                      >
                        Adicionar
                      </button>
                    </div>

                    <div className="lista-checkbox" style={{ marginTop: 12 }}>
                      {placas.length === 0 ? (
                        <div style={{ color: 'var(--tema-texto-4, #8a93a3)', fontSize: 13, gridColumn: 'span 3', fontStyle: 'italic' }}>
                          Nenhuma placa selecionada (será gerado para todas as placas).
                        </div>
                      ) : (
                        placas.map((p) => (
                          <label key={p} className="checkbox-item" title={p}>
                            <input
                              type="checkbox"
                              checked
                              onChange={() => removePlaca(p)}
                              disabled={loading}
                            />
                            <span className="checkbox-label">{p}</span>
                          </label>
                        ))
                      )}
                    </div>

                    {placas.length > 0 && (
                      <button
                        type="button"
                        className="filtro-reset-btn"
                        style={{ marginTop: 8, alignSelf: 'flex-start' }}
                        onClick={() => setPlacas([])}
                        disabled={loading}
                      >
                        Limpar seleção
                      </button>
                    )}
                  </div>

                  {/* Código Voluntário */}
                  <div className="filtro-field">
                    <label>Código Voluntário</label>
                    <input
                      type="text"
                      className="input-filtro"
                      placeholder="Ex: 1234"
                      value={codigoVoluntario}
                      onChange={(e) => setCodigoVoluntario(e.target.value)}
                      disabled={loading}
                    />
                  </div>

                  {/* Categoria */}
                  <div className="filtro-field">
                    <label>Categoria</label>
                    <input
                      type="text"
                      className="input-filtro"
                      placeholder="Ex: PASSEIO"
                      value={categoria}
                      onChange={(e) => setCategoria(e.target.value)}
                      disabled={loading}
                    />
                  </div>

                  {/* CPF Associado */}
                  <div className="filtro-field">
                    <label>CPF Associado</label>
                    <input
                      type="text"
                      className="input-filtro"
                      placeholder="Ex: 09166293766"
                      value={cpfAssociado}
                      onChange={(e) => setCpfAssociado(e.target.value)}
                      disabled={loading}
                    />
                  </div>

                  {/* Regional */}
                  <div className="filtro-field">
                    <label>Regional</label>
                    <input
                      type="text"
                      className="input-filtro"
                      placeholder="Ex: ATUAL CLUBE DE BENEFICIOS"
                      value={regional}
                      onChange={(e) => setRegional(e.target.value)}
                      disabled={loading}
                    />
                  </div>

                  {/* Nome do Associado (Full width in the 2-column grid) */}
                  <div className="filtro-field" style={{ gridColumn: 'span 2' }}>
                    <label>Nome do Associado</label>
                    <input
                      type="text"
                      className="input-filtro"
                      placeholder="Ex: amanda cataldi"
                      value={associadoNome}
                      onChange={(e) => setAssociadoNome(e.target.value)}
                      disabled={loading}
                    />
                  </div>

                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="filtro-modal-footer">
              <button
                type="button"
                className="filtro-btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="filtro-btn-primary"
                onClick={handleDownload}
                disabled={loading}
              >
                {loading ? 'Gerando arquivo...' : 'Gerar CSV'}
              </button>
              <button
                type="button"
                className="filtro-btn-primary"
                onClick={handleDownloadXlsx}
                disabled={loading}
              >
                {loading ? 'Gerando arquivo...' : 'Gerar XLSX'}
              </button>
            </div>

          </div>
        </div>
      </div>

      {loading && <FullScreenLoader />}
    </>
  );
};

export default ModalReportsPlaca;

